import React, { useEffect, useRef, useState, useCallback } from "react";
import {
  TextField,
  Stack,
  Tooltip,
  Typography,
  Button,
  Select,
  MenuItem,
} from "@mui/material";
import { useGoogleReCaptcha } from "react-google-recaptcha-v3";
import { isMobile } from "react-device-detect";
import CaptionAICard from "./captionAI-card.jsx";
import CaptionAIEditCaption from "./captionAI-edit-caption.jsx";
import SelectPlatformsDropdown from "../../components/generate-captions/select-platforms-dropdown.jsx";
import axios from "axios";
import LoadingSpinner from "../../components/common/loading-spinner.jsx";
import modalThemeTextStyle from "../../components/generate-captions/modal-theme-style.js";
import TogglesRow from "../../components/generate-captions/toggles-row.jsx";
import groupCaptions from "../../utils/group-captions.js";
import LoginPopup from "../sign-in/login-popup.jsx";
import WelcomeMessage from "../popups/welcome-message.jsx";
import SocialSuiteMessage from "../popups/social-suite-message.jsx";
import ProgressBar from "../../components/common/progress-bar.jsx";
import { captionAIHistorySubject } from "../../utils/subjects.js";
import "../../styles/generate-captions.scss";
import "../../styles/global.scss";

const buttonMargin = {
  margin: "0em 1em",
};

const CaptionAIForm = (props) => {
  const { executeRecaptcha } = useGoogleReCaptcha();
  const captionRef = useRef(null);
  const formRef = useRef(null);
  const [displayError, setDisplayError] = useState(false);
  const [description, setDescription] = useState(
    "Write a caption about growing a business using social media"
  );
  const [tone, setTone] = useState("Default");
  const [otherTone, setOtherTone] = useState("");
  const [platform, setPlatform] = useState([]);
  const [captionCount, setCaptionCount] = useState(3);
  const [captions, setCaptions] = useState([]);
  const [useHashtags, setUseHashtags] = useState(false);
  const [useEmojis, setUseEmojis] = useState(false);
  const [captionBeingEdited, setCaptionBeingEdited] = useState(null);
  const [isPlatformSpecific, setIsPlatformSpecific] = useState(false);
  const [formEdited, setFormEdited] = useState(true);
  const [loading, setLoading] = useState(false);
  const [showStickyButtons, setShowStickyButtons] = useState(false);
  const [showLoginPopup, setShowLoginPopup] = useState(false);
  const [initialTokenCount, setInitialTokenCount] = useState(() => {
    const storedCount = localStorage.getItem("initialTokenCount");
    return storedCount ? parseInt(storedCount) : 0;
  });
  const [showWelcomeMessage, setShowWelcomeMessage] = useState(false);
  const [showSocialSuiteLink, setShowSocialSuiteLink] = useState(false);
  const [currentSessionId, setCurrentSessionId] = useState(null);
  const [promptShownAfterCopy, setPromptShownAfterCopy] = useState(false);
  const [promptShownAfterCSV, setPromptShownAfterCSV] = useState(false);

  const toneOptions = [
    "Default",
    "Celebratory",
    "Empathetic",
    "Excited",
    "Formal",
    "Funny",
    "Witty",
    "Other",
  ];

  const resetForm = () => {
    setDisplayError(false);
    setDescription(
      "Write a caption about growing a business using social media"
    );
    setTone("Default");
    setOtherTone("");
    setPlatform([]);
    setCaptionCount(3);
    setCaptions([]);
    setUseHashtags(false);
    setUseEmojis(false);
    setCaptionBeingEdited(null);
    setIsPlatformSpecific(false);
    setFormEdited(true);
    setLoading(false);
    setShowLoginPopup(false);
    setCurrentSessionId(null);
  };

  const handleParamSubmit = () => {
    const urlWithParams = `https://app.cloudcampaign.com/captionAI?preview=true&description=${description}&platform=${platform.toString()}&tone=${tone}&otherTone=${otherTone}&captionCount=${captionCount}&useHashtags=${useHashtags}&useEmojis=${useEmojis}`;
    window.open(urlWithParams, "_blank");
  };

  const handleCopyClick = () => {
    props.handleCopyAlertOpen();

    if (!promptShownAfterCopy) {
      setShowSocialSuiteLink(true);
      setPromptShownAfterCopy(true);
    }
  };

  const handleRecaptcha = () => {
    if (!executeRecaptcha) {
      console.log("Execute recaptcha not yet available");
      return;
    }

    executeRecaptcha("exchangeForJWT").then((gReCaptchaToken) => {
      // exchange token for JWT
      axios
        .post(`/auth/login/recaptcha?token=${gReCaptchaToken}`, {})
        .then(() => {
          generateCaptions();
        })
        .catch((error) => {
          console.log(error);
          setDisplayError(true);
        });
    });
  };

  const handleSubmit = () => {
    if (props.preview) {
      handleParamSubmit();
      return;
    }

    if (props.contact) {
      generateCaptions();
    } else {
      handleRecaptcha();
    }
  };

  const handleLoginPopupOpen = () => {
    setShowLoginPopup(true);
  };

  const handleLoginPopupClosed = () => {
    setShowLoginPopup(false);
  };

  const handleDescriptionChange = (event) => {
    setDescription(event.target.value);
  };

  const handleToneChange = (event) => {
    setTone(event.target.value);
  };

  const handleOtherTone = (event) => {
    setOtherTone(event.target.value);
  };

  const handlePlatformChange = (event) => {
    const {
      target: { value },
    } = event;
    setPlatform(
      // On autofill we get a stringified value.
      typeof value === "string" ? value.split(",") : value
    );
  };

  const handleCaptionCountChange = (event) => {
    setCaptionCount(event.target.value);
  };

  const toggleHashtags = () => {
    setUseHashtags(!useHashtags);
  };

  const toggleEmojis = () => {
    setUseEmojis(!useEmojis);
  };

  const backToTop = () => {
    formRef.current.scrollIntoView();
  };

  const generateCaptions = () => {
    let numPlatforms = platform.length === 0 ? 1 : platform.length;
    let count = numPlatforms * captionCount;

    if (!props.contact) {
      setInitialTokenCount(initialTokenCount + 1);
    }

    if (initialTokenCount > 2 && !props.contact) {
      handleLoginPopupOpen();
      return;
    }

    if (
      props.tokenCount &&
      props.contact &&
      props.tokenCount?.current + count > props.tokenCount?.max
    ) {
      props.setShowTokenCountExceeded(true);
      return;
    }

    setFormEdited(true);
    setLoading(true);
    setCaptionBeingEdited(null);
    const captionCountNonZero = captionCount > 0 ? captionCount : 1;
    const captionCountBelowTen =
      captionCountNonZero < 11 ? captionCountNonZero : 10;
    const defaultToneCheck = tone === "Default" ? null : tone;

    const requestObject = {
      prompt: description,
      sentiment: defaultToneCheck === "Other" ? otherTone : defaultToneCheck,
      platformList: platform.length > 0 ? platform : null,
      numberOfCaptions: captionCountBelowTen,
      includeHashtags: useHashtags,
      includeEmojis: useEmojis,
    };

    axios
      .post("/captionai/generate", requestObject)
      .then((response) => {
        let sortedCaptions = groupCaptions(
          response.data?.session?.captionResults,
          captionCountBelowTen
        );
        setLoading(false);

        if (sortedCaptions[0].captions.length > 0) {
          setDisplayError(false);
          setCaptions(sortedCaptions);
          setTimeout(() => captionRef.current.scrollIntoView(), 100);
        } else {
          setDisplayError(true);
          setFormEdited(false);
        }

        if (props.tokenCount) {
          props.setTokenCount({
            current: response.data.tokens?.currentUsage,
            max: response.data.tokens?.maxUsage,
          });
        }
      })
      .catch((error) => {
        console.log(error);
        setLoading(false);
        setDisplayError(true);
        setFormEdited(false);
      });
  };

  const deleteCaption = (index) => {
    let newCaptions = [...captions];
    newCaptions.splice(index, 1);
    setCaptions(newCaptions);
  };

  const editCaption = (c) => {
    setCaptionBeingEdited(c);
  };

  const exportCaptions = () => {
    if (!promptShownAfterCSV) {
      setShowSocialSuiteLink(true);
      setPromptShownAfterCSV(true);
    }

    // Need a saved session in DB before we can export.
    if (currentSessionId) {
      axios
        .get(`/captionai/download/csv?sessionId=${currentSessionId}`)
        .then((response) => {
          const link = document.createElement("a");
          link.href = response.data;
          link.setAttribute("download", "Captions.csv");
          document.body.appendChild(link);
          link.click();
          link.remove();
        });
    } else {
      saveChat(true);
    }
  };

  const saveChat = (shouldExport) => {
    const captionCountNonZero = captionCount > 0 ? captionCount : 1;
    const captionCountBelowTen =
      captionCountNonZero < 11 ? captionCountNonZero : 10;
    const defaultToneCheck = tone === "Default" ? null : tone;
    const formattedCaptions = [];

    captions.forEach((capGroup) => {
      capGroup.captions.forEach((cap) => {
        formattedCaptions.push({
          caption: cap.text,
          id: cap.id,
          platform: cap.platform,
        });
      });
    });

    axios
      .put("/captionai/save", {
        captionResults: formattedCaptions,
        prompt: description,
        sentiment: defaultToneCheck === "Other" ? otherTone : defaultToneCheck,
        platformList: platform.length > 0 ? platform : null,
        numberOfCaptions: captionCountBelowTen,
        includeHashtags: useHashtags,
        includeEmojis: useEmojis,
        title: description,
      })
      .then((response) => {
        // using RxJS to update history state without waiting for useState async.
        captionAIHistorySubject.next([response.data]);
        setCurrentSessionId(response.data.id);

        let newSessionHistory = props.sessionHistory;

        if (props.sessionHistory?.results) {
          newSessionHistory.results.push(response.data);
        } else {
          newSessionHistory = {
            results: [response.data],
            page: 0,
            pageSize: 20,
            totalCount: 1,
          };
        }

        props.setSessionHistory(newSessionHistory);

        if (shouldExport) {
          axios
            .get(`/captionai/download/csv?sessionId=${response.data.id}`)
            .then((response) => {
              const link = document.createElement("a");
              link.href = response.data;
              link.setAttribute("download", "Captions.csv");
              document.body.appendChild(link);
              link.click();
              link.remove();
            });
        }
      })
      .catch((error) => {
        console.log(error);
      });
  };

  const checkOtherTone = (tone) => {
    if (toneOptions.includes(tone)) {
      return tone;
    } else {
      setOtherTone(tone);
      return "Other";
    }
  };

  const getTokenCount = () => {
    if (props.contact) {
      let numPlatforms = platform.length === 0 ? 1 : platform.length;
      let count = numPlatforms * captionCount;

      return `(${count} token${count > 1 ? "s" : ""})`;
    } else {
      return;
    }
  };

  useEffect(() => {
    setIsPlatformSpecific(platform.length > 0);
  }, [platform]);

  useEffect(() => {
    if (props.loadSession === null) {
      setFormEdited(false);
    } else {
      // restting loaded session so another can be clicked, and the regenerate captions button is active
      props.setLoadSession(null);
    }
  }, [
    description,
    tone,
    otherTone,
    useHashtags,
    useEmojis,
    platform,
    captionCount,
  ]);

  useEffect(() => {
    localStorage.setItem("initialTokenCount", initialTokenCount.toString());
  }, [initialTokenCount]);

  useEffect(() => {
    if (props.clearForm) {
      resetForm();
      props.setClearForm(false);
    }
  }, [props.clearForm]);

  useEffect(() => {
    if (props.loadSession) {
      let platformsInSession = [];

      props.loadSession.captionResults?.forEach((c) => {
        if (
          !platformsInSession.includes(c.platform) &&
          c.platform &&
          c.platform !== "null"
        ) {
          platformsInSession.push(c.platform);
        }
      });

      const numPlatforms =
        platformsInSession.length === 0 ? 1 : platformsInSession.length;
      const numCaptions =
        props.loadSession.captionResults?.length / numPlatforms;

      setDescription(props.loadSession.prompt);
      setTone(
        props.loadSession.sentiment === null
          ? "Default"
          : checkOtherTone(props.loadSession.sentiment)
      );
      setCaptionCount(numCaptions);
      setCaptions(groupCaptions(props.loadSession.captionResults, numCaptions));
      setPlatform(platformsInSession);
      setCaptionBeingEdited(null);
      setFormEdited(true);
      setDisplayError(false);
      setCurrentSessionId(props.loadSession.id);
    }
  }, [props.loadSession]);

  useEffect(() => {
    if (captions.length > 0 && !loading && props.contact) {
      setShowStickyButtons(true);
    } else {
      setShowStickyButtons(false);
    }
  }, [captions, loading, props.contact]);

  useEffect(() => {
    if (props.sessionFromParams) {
      const toneCheck =
        props.sessionFromParams.tone === "null"
          ? "Default"
          : props.sessionFromParams.tone;
      const otherToneCheck =
        props.sessionFromParams.otherTone === "null"
          ? ""
          : props.sessionFromParams.otherTone;
      const platformCheck =
        props.sessionFromParams.platform[0] === ""
          ? []
          : props.sessionFromParams.platform;

      setDescription(props.sessionFromParams.description);
      setTone(toneCheck);
      setOtherTone(otherToneCheck);
      setCaptionCount(props.sessionFromParams.captionCount);
      setUseHashtags(props.sessionFromParams.useHashtags);
      setUseEmojis(props.sessionFromParams.useEmojis);
      setPlatform(platformCheck);

      setShowWelcomeMessage(true);
    }
  }, [props.sessionFromParams]);

  return (
    <div
      className={
        props.contact
          ? "left-caption-input generate-captions-container"
          : "generate-captions-container"
      }
    >
      {props.tokenCount ? (
        <Stack
          direction="row"
          justifyContent="space-between"
          className="progress-bar-margin"
        >
          <ProgressBar
            current={props.tokenCount?.current}
            max={props.tokenCount?.max}
          />
          <Stack direction="row">
            <Button
              variant="outlined"
              color="cc"
              onClick={() => saveChat(false)}
              sx={buttonMargin}
              disabled={captions?.length === 0}
            >
              Save Results
            </Button>
            <Button
              variant="contained"
              color="cc"
              onClick={exportCaptions}
              sx={buttonMargin}
              disabled={captions?.length === 0}
            >
              Export to CSV
            </Button>
          </Stack>
        </Stack>
      ) : (
        <></>
      )}
      <span ref={formRef}></span>
      <LoginPopup
        open={showLoginPopup}
        handleClose={handleLoginPopupClosed}
        handleOpen={handleLoginPopupOpen}
        toggleSignIn={props.toggleSignIn}
      />
      <WelcomeMessage
        open={showWelcomeMessage}
        handleSubmit={handleSubmit}
        setShowWelcomeMessage={setShowWelcomeMessage}
      />
      <SocialSuiteMessage
        open={showSocialSuiteLink}
        setShowSocialSuiteLink={setShowSocialSuiteLink}
      />
      <div className="form-container">
        <div className="input-row">
          <Stack direction="column" className="full-row-item">
            <h5>Enter prompt for caption generation</h5>
            <TextField
              fullWidth
              variant="outlined"
              onChange={handleDescriptionChange}
              value={description}
            />
          </Stack>
        </div>

        <div className="input-row">
          <Stack direction="column" className="full-row-item">
            <h5>Select platform(s)</h5>
            <SelectPlatformsDropdown
              platform={platform}
              handlePlatformChange={handlePlatformChange}
            />
          </Stack>
        </div>

        <div className="input-row">
          <Stack direction="column" className="third-row-item">
            <h5>Select a tone</h5>
            <Select
              value={tone}
              label="Tone"
              onChange={handleToneChange}
              className="generate-captions-input"
              inputProps={{ "data-testid": "captionAI-tone" }}
              native={isMobile || false}
            >
              {toneOptions.map((option) =>
                isMobile ? (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ) : (
                  <MenuItem key={option} value={option}>
                    {option}
                  </MenuItem>
                )
              )}
            </Select>
          </Stack>
          {tone === "Other" ? (
            <Stack direction="column" className="third-row-item">
              <h5>&nbsp;</h5>
              <TextField
                fullWidth
                variant="outlined"
                onChange={handleOtherTone}
                value={otherTone}
              />
            </Stack>
          ) : (
            <></>
          )}
          <Stack direction="column" className="third-row-item">
            <h5>
              Caption count
              <Tooltip
                title={
                  <Typography fontSize={14}>
                    Generate up to 10 captions
                  </Typography>
                }
                placement="top"
              >
                <i className="fa fa-info-circle font-awesome-icon-style-light"></i>
              </Tooltip>
            </h5>
            <TextField
              fullWidth
              variant="outlined"
              onChange={handleCaptionCountChange}
              value={captionCount}
              type="number"
              error={captionCount > 10 || captionCount < 1}
              InputProps={{
                inputProps: {
                  min: 1,
                  max: 10,
                  inputMode: "numeric",
                  pattern: "[1-10]*",
                },
              }}
              placeholder="3"
              className="generate-captions-input clickable"
            />
          </Stack>
        </div>
        <Stack className="toggles-margin">
          <TogglesRow
            toggleHashtags={toggleHashtags}
            useHashtags={useHashtags}
            toggleEmojis={toggleEmojis}
            useEmojis={useEmojis}
          />
        </Stack>

        <Button
          variant="contained"
          color="cc"
          fullWidth
          onClick={handleSubmit}
          disabled={formEdited}
          data-testid={"captionAI-submit"}
        >
          Generate Captions
          <span className="token-count-style">{getTokenCount()}</span>
        </Button>
      </div>

      <div className="captions-group">
        {captions.length > 0 && !loading ? (
          <Stack direction="column" ref={captionRef}>
            {captionBeingEdited ? (
              <CaptionAIEditCaption
                isPlatformSpecific={isPlatformSpecific}
                captionGroup={captionBeingEdited}
                editCaption={editCaption}
                captions={captions}
                hashtags={[]}
              />
            ) : (
              <>
                <Stack direction="row" justifyContent="center" margin={1}>
                  <Button
                    variant="contained"
                    color="cc"
                    onClick={generateCaptions}
                    disabled={!formEdited}
                  >
                    Regenerate Captions
                    <span className="token-count-style">{getTokenCount()}</span>
                  </Button>
                </Stack>
                {captions.map((captionGroup, index) => {
                  return (
                    <CaptionAICard
                      captionGroup={captionGroup}
                      deleteCaption={deleteCaption}
                      editCaption={editCaption}
                      captions={captions}
                      setCaptions={setCaptions}
                      isPlatformSpecific={isPlatformSpecific}
                      index={index}
                      handleCopyClick={handleCopyClick}
                    />
                  );
                })}
                <Stack direction="row" justifyContent="center">
                  <Button variant="text" onClick={backToTop}>
                    Back to top
                  </Button>
                </Stack>
              </>
            )}
          </Stack>
        ) : (
          <></>
        )}
        {displayError && !loading ? (
          <Stack direction="row" justifyContent="center">
            <h5 style={modalThemeTextStyle}>
              We've run into an issue generating captions. Please try again.
            </h5>
          </Stack>
        ) : (
          <></>
        )}
        {loading ? (
          <div className="platform-tabs-margin">
            <LoadingSpinner size={35} />
          </div>
        ) : (
          <></>
        )}
      </div>
    </div>
  );
};

export default CaptionAIForm;
